import { DataDiscoveryActionTakenBy, DataDiscoveryActionTypes } from './workflowAutomationSlice'
import type { DsrWorkflowParams, WorkflowListItem } from './workflowAutomationSlice'
import {
  BlockType,
  ConditionalFlow,
  DSRFormData,
  DSRFormDataItem,
  dsrWFDefaultDatasourceType,
  DsrWorkflowApiBlockTypes,
  EmailIdVerification,
  initialDSRWorkflowStepData,
  stepDataDiscoveryValidation,
  stepReportGeneration,
  stepRequestApproval,
  Workflow
} from './createEditWorkflow/types'
import { API_DSR_REQUEST_TYPES, DATA_SOURCE_TYPES } from '../../../constants'
import { DsrRequestType } from '../../../services/graphqlSchemaTypes'
import { DsrRequestApiKeys } from '../dsrForms/dsrFormsSlice'
import { gql } from 'graphql-request'

export const queryDsrWFEmailTemplates = (params: {
  eventType: string
  requestType: API_DSR_REQUEST_TYPES
}) => {
  const { eventType, requestType } = params
  const formattedRequestType =
    requestType === API_DSR_REQUEST_TYPES.DO_NOT_SELL
      ? DsrRequestApiKeys.doNotSell
      : requestType === API_DSR_REQUEST_TYPES.OPT_OUT
      ? DsrRequestApiKeys.optOut
      : requestType.toLowerCase()

  return gql`
    {
      dsrEmailTemplate(
        filter: [
          { key: REQUEST_TYPE, values: [${JSON.stringify(formattedRequestType)}] }
          { key: EVENT_TYPE, values: [${JSON.stringify(eventType)}] }
        ]
      ) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  `
}

export const mapQueryDsrWFEmailTemplates = (raw: any): any => {
  try {
    const emailTemplates = raw.dsrEmailTemplate.edges.map(({ node }) => node)
    return emailTemplates
  } catch (error) {
    return []
  }
}

export const queryDsrDefaultDatasources = () => {
  return gql`
    {
      dsrSetting {
        edges {
          node {
            defaultDsrDatasource {
              edges {
                node {
                  id
                  name
                  type
                  createdBy
                  dsrErasureEnabled
                  dsrRectificationEnabled
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryDsrDefaultDatasources = (
  raw: any,
  requestType?: DsrRequestType
): dsrWFDefaultDatasourceType[] => {
  try {
    const defaultDatasources = raw.dsrSetting.edges[0].node.defaultDsrDatasource.edges.map(
      ({ node }) => {
        let actionToBeTaken: DataDiscoveryActionTypes =
          DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE

        // Determine actionToBeTaken based on node type and request type
        if (node.type === DATA_SOURCE_TYPES.remote) {
          actionToBeTaken = DataDiscoveryActionTypes.MANUALLY_DISCOVER_AND_VALIDATE
        } else if (node?.dsrErasureEnabled) {
          if (requestType === DsrRequestType.Erasure) {
            actionToBeTaken = DataDiscoveryActionTypes.AUTO_DISCOVER_AND_ERASE
          } else {
            actionToBeTaken = DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE
          }
        } else if (node?.dsrRectificationEnabled) {
          if (requestType === DsrRequestType.Rectification) {
            actionToBeTaken = DataDiscoveryActionTypes.AUTO_DISCOVER_AND_RECTIFY
          } else {
            actionToBeTaken = DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE
          }
        } else {
          actionToBeTaken = DataDiscoveryActionTypes.AUTO_DISCOVER_AND_VALIDATE
        }

        const actionToBeTakenBy =
          node.type === DATA_SOURCE_TYPES.remote
            ? DataDiscoveryActionTakenBy.DATA_SOURCE_OWNER
            : 'SYSTEM'

        return {
          id: node.id,
          name: node.name,
          type: node.type,
          owner: node.createdBy,
          actionToBeTaken,
          actionToBeTakenBy,
          dsrErasureSupported: node?.dsrErasureEnabled || false,
          dsrRecitificationSupported: node?.dsrRectificationEnabled || false
        }
      }
    )
    if (defaultDatasources.length > 0) {
      return defaultDatasources
    } else {
      return []
    }
  } catch (error) {
    return []
  }
}

export const queryDsrWorkflows = () => {
  return gql`
    {
      flow(filter: [{ key: TYPE, values: ["DSR_WORKFLOW"] }]) {
        count
        edges {
          node {
            id
            name
            type
            additionalMetadata {
              dsrRequestType
            }
            createdBy
            createdAt
            updateTimestamp
            attachedDsrFormCount
          }
        }
      }
    }
  `
}

export const mapDsrWorkflows = (res: any) => {
  try {
    const list: WorkflowListItem[] = res.flow?.edges?.map(({ node }) => {
      return {
        id: node.id,
        name: node.name,
        createdAt: node.createdAt,
        lastUpdated: node.updateTimestamp,
        createdBy: node.createdBy,
        requestType: node.additionalMetadata?.dsrRequestType,
        attachedDsrFormCount: node.attachedDsrFormCount
      }
    })

    return { list, totalCount: res.flow.count }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in mapDsrWorkflows', error)
    return {
      list: [],
      totalCount: 0
    }
  }
}

export const queryDsrWorkflowById = (workflowId: string) => {
  return gql`
    {
      flow(id: "${workflowId}", filter: [{ key: TYPE, values: ["DSR_WORKFLOW"] }]) {
        edges {
          node {
            id
            name
            type
            additionalMetadata {
              dsrRequestType
            }
            createdBy
            createdAt
            updateTimestamp
            attachedDsrFormCount
            blocks {
              id
              order
              type
              nextBlocks
              isRoot
              isDisabled
              config {
                __typename
                ... on DSRIdVerificationObject {
                  sendIdVerificationAndSubmissionEmail
                  requestSubmissionEmailTemplateID
                  IdVerificationEmailTemplateID
                  automaticRejectionIfIDNotVerified
                  automaticRejectAfterDays
                  automaticRejectEmailTemplateID
                }
                ... on DSRRequestApprovalObject {
                  autoApprove
                  sendApprovalMail
                  approvalEmailTemplateID
                  sendAdditionalEmails
                  additionalEmailFields
                }
                ... on DSRDataDiscoveryAndValidationObject {
                  dataSourceConfigs {
                    dataSourceId
                    actionToBeTaken
                    actionToBeTakenBy
                  }
                }
                ... on DSRReportSharingAndClosureObject {
                  autoShareReportWithDataSubject
                  reportSharingEmailTemplateID
                  passwordSharingEmailTemplateID
                  sendAdditionalEmailsForReportSharing
                  additionalEmailFieldsForReportSharing
                  autoCloseRequest
                  sendClosureEmail
                  closureEmailTemplateID
                  sendAdditionalEmails
                  additionalEmailFields
                }
                ... on ConditionBlockConfig {
                  nextBlockOnTrue
                  conditionalLogic {
                    operator
                    groups {
                      operator
                      conditions {
                        field
                        operator
                        targetValues
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      dsrSetting {
        edges {
          node {
            defaultDsrDatasource {
              edges {
                node {
                  id
                }
              }
            }
          }
        }
      }
      datasources {
        edges {
          node {
            id
            name
            type
            createdBy
            dsrErasureEnabled
            dsrRectificationEnabled
          }
        }
      }
    }
  `
}

export const mapQueryWorkflowById = (
  response: any
): { workflow: Workflow; blockConfig: DSRFormData; stepData } => {
  const node = response.flow.edges[0]?.node

  const datasources = response.datasources.edges.map((edge) => edge.node)

  // 1. Initialize Workflow State
  const workflow: Workflow = {
    workflowAutomationName: node.name,
    workflowAutomationId: node.id,
    requestType: node.additionalMetadata?.dsrRequestType,
    currenUserEmailId: node.createdBy || '',
    workflowAutomationNameError: false,
    createdBy: node.createdBy || '',
    createdAt: node.createdAt || '',
    updateTimestamp: node.updateTimestamp || '',
    attachedDsrFormCount: node.attachedDsrFormCount || 0,
    stepEmailIdVerification: {},
    stepRequestApproval: {},
    stepDataDiscoveryValidation: {},
    stepReportGeneration: {}
  }

  const stepData = {
    stepEmailIdVerification: {},
    stepRequestApproval: {},
    stepDataDiscoveryValidation: {},
    stepReportGeneration: {}
  }

  // 2. Map of all blocks for quick lookup
  const blockMap = new Map()
  const nestedInConditionIds = new Set<string>()

  node.blocks.forEach((block) => {
    blockMap.set(block.id, block)
    // Track which blocks are children of a conditional block
    if (block.type === DsrWorkflowApiBlockTypes.CONDITIONAL) {
      block.nextBlocks?.forEach((id: string) => nestedInConditionIds.add(id))
    }
  })

  // 3. Mapping Helpers (same as your logic but cleaned up)
  const mapEmailIdVerification = (block: any): EmailIdVerification => ({
    id: block.id,
    enabled: !block.isDisabled,
    sendAcknowledgement: block.config.sendIdVerificationAndSubmissionEmail || false,
    requestSubmittedEmailTemplateId: block.config.requestSubmissionEmailTemplateID,
    emailVerificationSuccessEmailTemplateId: block.config.IdVerificationEmailTemplateID,
    requestSubmittedEmailTemplateError: false,
    emailVerificationSuccessEmailTemplateError: false
  })

  const mapRequestApproval = (block): stepRequestApproval => ({
    id: block.id,
    enabled: !block.isDisabled,
    approveRequest: block.config.autoApprove || false,
    sendApprovalEmail: block.config.sendApprovalMail || false,
    requestApprovalEmailTemplateId: block.config.approvalEmailTemplateID,
    requestApprovalEmailTemplateError: false,
    sendAdditionalEmails: block.config.sendAdditionalEmails || false,
    additionalEmailFields: block.config.additionalEmailFields || []
  })

  const mapDataDiscovery = (block): stepDataDiscoveryValidation => ({
    id: block.id,
    enabled: !block.isDisabled,
    dsrDefaultDatasources: (block.config.dataSourceConfigs || []).map((ds: any) => {
      const matchingDs = datasources.find((d) => d.id === ds.dataSourceId)
      return {
        id: ds.dataSourceId,
        name: matchingDs?.name || '',
        type: matchingDs?.type || '',
        owner: matchingDs?.createdBy || '',
        actionToBeTaken: ds.actionToBeTaken as DataDiscoveryActionTypes,
        actionToBeTakenBy: ds.actionToBeTakenBy,
        dsrErasureSupported: matchingDs?.dsrErasureEnabled || false,
        dsrRecitificationSupported: matchingDs?.dsrRectificationEnabled || false
      }
    })
  })

  const mapReportGeneration = (block): stepReportGeneration => ({
    id: block.id,
    enabled: !block.isDisabled,
    shareReportWithDataSubject: block.config.autoShareReportWithDataSubject || false,
    shareReportEmailTemplateId: block.config.reportSharingEmailTemplateID,
    reportPasswordEmailTemplateId: block.config.passwordSharingEmailTemplateID,
    sendAdditionalEmailsForReportSharing:
      block.config.sendAdditionalEmailsForReportSharing || false,
    additionalEmailFieldsForReportSharing: block.config.additionalEmailFieldsForReportSharing || [],
    additionalEmailFieldsForReportSharingError: false,
    automaticallyCloseRequest: block.config.autoCloseRequest || false,
    sendClosureEmail: block.config.sendClosureEmail || false,
    closureEmailTemplateId: block.config.closureEmailTemplateID,
    closureEmailTemplateError: false,
    reportPasswordEmailTemplateError: false,
    reportSharingEmailTemplateError: false,
    sendAdditionalEmails: block.config.sendAdditionalEmails || false,
    additionalEmailFields: block.config.additionalEmailFields || []
  })

  // 4. Populate Workflow Configuration Map
  node.blocks.forEach((block: any) => {
    switch (block.type) {
      case DsrWorkflowApiBlockTypes.DSR_ID_VERIFICATION:
        workflow.stepEmailIdVerification[block.id] = mapEmailIdVerification(block)
        stepData.stepEmailIdVerification[block.id] =
          initialDSRWorkflowStepData.stepEmailIdVerification
        break
      case DsrWorkflowApiBlockTypes.DSR_REQUEST_APPROVAL:
        workflow.stepRequestApproval[block.id] = mapRequestApproval(block)
        stepData.stepRequestApproval[block.id] = initialDSRWorkflowStepData.stepRequestApproval
        break
      case DsrWorkflowApiBlockTypes.DSR_DATA_DISCOVERY:
        workflow.stepDataDiscoveryValidation[block.id] = mapDataDiscovery(block)
        stepData.stepDataDiscoveryValidation[block.id] =
          initialDSRWorkflowStepData.stepDataDiscoveryValidation
        break
      case DsrWorkflowApiBlockTypes.DSR_REPORT_SHARING_AND_CLOSURE:
        workflow.stepReportGeneration[block.id] = mapReportGeneration(block)
        stepData.stepReportGeneration[block.id] = initialDSRWorkflowStepData.stepReportGeneration
        break
    }
  })

  // 5. Build Enriched UI Config
  const getUIBlockType = (apiType: string): BlockType => {
    switch (apiType) {
      case DsrWorkflowApiBlockTypes.DSR_ID_VERIFICATION:
        return BlockType.STEP_EMAIL_ID_VERIFICATION
      case DsrWorkflowApiBlockTypes.DSR_REQUEST_APPROVAL:
        return BlockType.STEP_REQUEST_APPROVAL
      case DsrWorkflowApiBlockTypes.DSR_DATA_DISCOVERY:
        return BlockType.STEP_DATA_DISCOVERY_VALIDATION
      case DsrWorkflowApiBlockTypes.DSR_REPORT_SHARING_AND_CLOSURE:
        return BlockType.STEP_REPORT_GENERATION
      case DsrWorkflowApiBlockTypes.CONDITIONAL:
        return BlockType.COND_BLOCK
      default:
        return BlockType.CONNECTOR
    }
  }

  const createUIItem = (block: any): DSRFormDataItem => {
    const type = getUIBlockType(block.type)
    if (type === BlockType.COND_BLOCK) {
      const condFlow: ConditionalFlow = {
        id: block.id,
        type: BlockType.COND_BLOCK,
        data: {
          operator: block.config.conditionalLogic?.operator,
          groups: block.config.conditionalLogic?.groups || []
        },
        // Map the child blocks referenced by the condition
        blocks: (block.nextBlocks || []).map((childId: string) => {
          const child = blockMap.get(childId)
          return {
            id: child.id,
            enabled: !child.isDisabled,
            type: getUIBlockType(child.type)
          }
        })
      }
      return { type: BlockType.COND_BLOCK, data: condFlow }
    }
    return {
      type,
      data: { id: block.id, enabled: !block.isDisabled, type }
    }
  }

  // Identify main-track blocks (those not inside a conditional path)
  // We filter out any block that was found in a CONDITIONAL block's nextBlocks
  const mainTrackBlocks = node.blocks
    .filter((b: any) => !nestedInConditionIds.has(b.id))
    .sort((a: any, b: any) => a.order - b.order)

  const enrichedBlockConfig: DSRFormData = []

  mainTrackBlocks.forEach((item, index) => {
    if (index > 0) enrichedBlockConfig.push({ type: BlockType.CONNECTOR })
    if (getUIBlockType(item.type) !== BlockType.COND_BLOCK) {
      enrichedBlockConfig.push({ type: BlockType.ADD_CONDITION })
      enrichedBlockConfig.push({ type: BlockType.CONNECTOR })
    }
    enrichedBlockConfig.push(createUIItem(item))
    if (
      getUIBlockType(item.type) === BlockType.COND_BLOCK &&
      index === mainTrackBlocks.length - 1
    ) {
      enrichedBlockConfig.push({ type: BlockType.ADD_CONDITION })
    }
  })
  return { workflow, blockConfig: enrichedBlockConfig, stepData }
}

const getNextBlockFragment = (nextBlockIds: string[]): string => {
  return nextBlockIds.length
    ? `nextBlocks: [${nextBlockIds.map((id) => `"${id}"`).join(', ')}]`
    : ''
}

const getFragmentEmailIdVerification = (
  block: EmailIdVerification,
  nextBlockIds: string[],
  isRoot: boolean
) => {
  const requestSubmissionEmailTemplateID = block.requestSubmittedEmailTemplateId
    ? `requestSubmissionEmailTemplateID: "${block.requestSubmittedEmailTemplateId || ''}"`
    : ''
  const emailVerificationSuccessEmailTemplateId = block.emailVerificationSuccessEmailTemplateId
    ? `IdVerificationEmailTemplateID: "${block.emailVerificationSuccessEmailTemplateId || ''}"`
    : ''
  const nextBlocksString = nextBlockIds.length
    ? `nextBlocks: [${nextBlockIds.map((id) => `"${id}"`).join(', ')}]`
    : ''
  return `{
            id: "${block.id || ''}"
            type: ${DsrWorkflowApiBlockTypes.DSR_ID_VERIFICATION}
            isRoot: ${isRoot}
            order: 1
            ${nextBlocksString}
            isDisabled: ${!block.enabled}
            dsrIdVerificationConfig: {
              sendIdVerificationAndSubmissionEmail: ${block.sendAcknowledgement}
              ${requestSubmissionEmailTemplateID}
              ${emailVerificationSuccessEmailTemplateId}
              automaticRejectionIfIDNotVerified: true
              automaticRejectAfterDays: 15
            }
          }`
}

const getFragmentRequestApproval = (
  block: stepRequestApproval,
  nextBlockIds: string[],
  isRoot: boolean
) => {
  const requestApprovalEmailTemplateId = block.requestApprovalEmailTemplateId
    ? `approvalEmailTemplateID: "${block.requestApprovalEmailTemplateId || ''}"`
    : ''
  const nextBlocksString = getNextBlockFragment(nextBlockIds)
  return `{
    id: "${block.id || ''}"
    type: ${DsrWorkflowApiBlockTypes.DSR_REQUEST_APPROVAL}
    isRoot: ${isRoot}
    order: 2
    ${nextBlocksString}
    isDisabled: ${!block.enabled}
    dsrRequestApprovalConfig: {
      autoApprove: ${block.approveRequest}
      sendApprovalMail: ${block.sendApprovalEmail}
      ${requestApprovalEmailTemplateId}
      sendAdditionalEmails: ${!!block.sendAdditionalEmails}
      additionalEmailFields: ${JSON.stringify(block.additionalEmailFields || [])}
    }
  }`
}

const getFragmentDataDiscovery = (
  block: stepDataDiscoveryValidation,
  nextBlockIds: string[],
  isRoot: boolean
) => {
  const dataSourceConfigs = (block.dsrDefaultDatasources || [])
    .map(
      (ds) => `{
    dataSourceId: "${ds.id}"
    actionToBeTaken: ${ds.actionToBeTaken}
    actionToBeTakenBy: ${ds.actionToBeTakenBy}
  }`
    )
    .join(', ')

  const nextBlocksString = getNextBlockFragment(nextBlockIds)

  return `{
    id: "${block.id || ''}"
    type: ${DsrWorkflowApiBlockTypes.DSR_DATA_DISCOVERY}
    isRoot: ${isRoot}
    order: 3
    ${nextBlocksString}
    isDisabled: ${!block.enabled}
    dsrDataDiscoveryAndValidationConfig: {
      dataSourceConfigs: [${dataSourceConfigs}]
    }
  }`
}

const getFragmentReportGeneration = (
  block: stepReportGeneration,
  nextBlockIds: string[],
  isRoot: boolean
) => {
  const shareReportEmailTemplateId = block.shareReportEmailTemplateId
    ? `reportSharingEmailTemplateID: "${block.shareReportEmailTemplateId || ''}"`
    : ''
  const reportPasswordEmailTemplateId = block.reportPasswordEmailTemplateId
    ? `passwordSharingEmailTemplateID: "${block.reportPasswordEmailTemplateId || ''}"`
    : ''
  const closureEmailTemplateId = block.closureEmailTemplateId
    ? `closureEmailTemplateID: "${block.closureEmailTemplateId || ''}"`
    : ''
  const nextBlocksString = getNextBlockFragment(nextBlockIds)
  return `{
    id: "${block.id || ''}"
    type: ${DsrWorkflowApiBlockTypes.DSR_REPORT_SHARING_AND_CLOSURE}
    isRoot: ${isRoot}
    order: 4
    ${nextBlocksString}
    isDisabled: ${!block.enabled}
    dsrReportSharingAndClosureConfig: {
      autoShareReportWithDataSubject: ${block.shareReportWithDataSubject}
      ${shareReportEmailTemplateId}
      ${reportPasswordEmailTemplateId}
      sendAdditionalEmailsForReportSharing: ${!!block.sendAdditionalEmailsForReportSharing}
      additionalEmailFieldsForReportSharing: ${JSON.stringify(
        block.additionalEmailFieldsForReportSharing || []
      )}
      ${closureEmailTemplateId}
      autoCloseRequest: ${block.automaticallyCloseRequest}
      sendClosureEmail: ${block.sendClosureEmail}
      sendAdditionalEmails: ${!!block.sendAdditionalEmails}
      additionalEmailFields: ${JSON.stringify(block.additionalEmailFields || [])}
    }
  }`
}

const getFragmentConditional = (
  block: ConditionalFlow,
  nextBlockIds: string[],
  isRoot: boolean,
  nextBlockOnTrue: string
) => {
  const groupsFragment = block.data?.groups?.map((group) => {
    const conditions = group.conditions
      .map((condition) => {
        const targetValues = condition.targetValues?.length
          ? `targetValues: ${JSON.stringify(condition.targetValues || [])}`
          : ''
        return `{
          field: "${condition.field}"
          operator: ${condition.operator}
          ${targetValues}
        }`
      })
      .join(', ')

    return `{
      operator: ${group.operator}
      conditions: [${conditions}]
    }`
  })

  const nextBlocksString = getNextBlockFragment(nextBlockIds)

  return `{
    id: "${block.id}"
    type: ${DsrWorkflowApiBlockTypes.CONDITIONAL}
    isRoot: ${isRoot}
    order: 5
    ${nextBlocksString}
    isDisabled: false
    conditionalLogicConfig: {
      ${nextBlockOnTrue ? `nextBlockOnTrue: "${nextBlockOnTrue}"` : ''}
      conditionalLogic: {
        operator: ${block.data.operator},
        groups: [${groupsFragment}]
      }
    }
  }`
}

const getWorkflowMutationPayload = (params: DsrWorkflowParams) => {
  const { workflow, blockConfig } = params
  const fragments: string[] = []

  const processItems = (items: any[], isRootLevel?: boolean) => {
    const blocks = items.filter((item) => !!item.data)

    // Check if the first block is conditional
    const firstBlockIsConditional = blocks.length > 0 && blocks[0].type === BlockType.COND_BLOCK

    blocks.forEach((item, i) => {
      const blockId = item.data.id

      // Determine isRoot:
      // - If first block is conditional, all conditional blocks are root
      // - Otherwise, only the first block at root level is root
      let isRoot: boolean
      if (firstBlockIsConditional && item.type === BlockType.COND_BLOCK) {
        isRoot = !!isRootLevel
      } else {
        isRoot = !!isRootLevel && i === 0
      }

      const nextBlockIds: string[] = []

      if (i + 1 < blocks.length) {
        const nextBlock = blocks[i + 1]

        // If first block is NOT conditional and next block is conditional,
        // collect ALL consecutive conditionals as children of current block
        if (!firstBlockIsConditional && nextBlock.type === BlockType.COND_BLOCK) {
          let j = i + 1
          while (j < blocks.length && blocks[j].type === BlockType.COND_BLOCK) {
            nextBlockIds.push(blocks[j].data.id)
            j++
          }
        } else if (firstBlockIsConditional && item.type === BlockType.COND_BLOCK) {
          // When first is conditional and current is conditional,
          // skip other conditionals - they are siblings (each is root)
          // No nextBlockIds for conditional blocks in this case
        } else {
          // Single next block (non-conditional)
          nextBlockIds.push(nextBlock.data.id)
        }
      }

      if (item.type === BlockType.STEP_EMAIL_ID_VERIFICATION) {
        const data = {
          ...workflow.stepEmailIdVerification[blockId],
          id: blockId,
          enabled: item.data.enabled
        }
        fragments.push(getFragmentEmailIdVerification(data, nextBlockIds, isRoot))
      } else if (item.type === BlockType.STEP_REQUEST_APPROVAL) {
        const data = {
          ...workflow.stepRequestApproval[blockId],
          id: blockId,
          enabled: item.data.enabled
        }
        fragments.push(getFragmentRequestApproval(data, nextBlockIds, isRoot))
      } else if (item.type === BlockType.STEP_DATA_DISCOVERY_VALIDATION) {
        const data = {
          ...workflow.stepDataDiscoveryValidation[blockId],
          id: blockId,
          enabled: item.data.enabled
        }
        fragments.push(getFragmentDataDiscovery(data, nextBlockIds, isRoot))
      } else if (item.type === BlockType.STEP_REPORT_GENERATION) {
        const data = {
          ...workflow.stepReportGeneration[blockId],
          id: blockId,
          enabled: item.data.enabled
        }
        fragments.push(getFragmentReportGeneration(data, nextBlockIds, isRoot))
      } else if (item.type === BlockType.COND_BLOCK) {
        const nestedBlocks = item.data?.blocks || []
        const allNextBlockIds = nestedBlocks.map((b: any) => b.id).filter(Boolean)
        const nextBlockOnTrue = allNextBlockIds[0] || ''

        fragments.push(getFragmentConditional(item.data, allNextBlockIds, isRoot, nextBlockOnTrue))
        if (item.data.blocks) {
          processItems(item.data.blocks.map((b: any) => ({ ...b, data: b })))
        }
      }
    })
  }

  processItems(blockConfig, true)

  return fragments
}

export const mutationSaveWorkflow = (params: DsrWorkflowParams) => {
  const { workflow } = params
  const fragments = getWorkflowMutationPayload(params)

  return gql`
    mutation {
      createFlow(
        clientMutationId: "save-dsr-workflow"
        inputData: {
          name: ${JSON.stringify(workflow.workflowAutomationName)}
          type: DSR_WORKFLOW
          additionalMetadata: {dsrRequestType: ${workflow.requestType}}
          blocks: [ ${fragments.join('\n')} ]
        }
      ) {
        clientMutationId
        flowId
      }
    }
  `
}

export const mutationUpdateWorkflow = (params: DsrWorkflowParams) => {
  const { workflow } = params
  const fragments = getWorkflowMutationPayload(params)

  return gql`
    mutation {
      updateFlow(
        clientMutationId: "update-dsr-workflow"
        flowId: "${workflow.workflowAutomationId}"
        inputData: {
          name: ${JSON.stringify(workflow.workflowAutomationName)}
          type: DSR_WORKFLOW
          additionalMetadata: {dsrRequestType: ${workflow.requestType}}
          blocks: [ ${fragments.join('\n')} ]
        }
      ) {
        clientMutationId
        flowId
      }
    }
  `
}

export const mutationDeleteWorkflow = (workflowId: string) => {
  return gql`
    mutation {
      deleteFlow(
        clientMutationId: "delete-dsr-workflow"
        flowId: "${workflowId}"
      ) {
        clientMutationId
        flowId
      }
    }
  `
}
